import React from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Stack, Divider, Typography, Card, CardContent, IconButton, Alert } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useUpdateSection, useDeleteSection, useSectionQuery } from 'controllers/react-query';
import { EditableInputField } from 'components/EditableInputField';
import { useFeatures } from 'components/FeaturesContext';
import { LessonSection } from 'controllers/types';
import { useModal } from 'components/utils/ModalContext';
import { EditSegmentField } from './EditSegmentField';
import { SectionDetailSkeleton } from './SectionDetailSkeleton';

function Header({ section, showDeleteButton = true }: { section: LessonSection; showDeleteButton?: boolean }) {
  const { lessonId } = useParams<{ lessonId: string }>();
  const {
    mutate: updateTopic,
    isPending,
    error,
    reset: resetTopicMutation,
  } = useUpdateSection(section.id, Number(lessonId));
  const { mutate: deleteSection } = useDeleteSection(section.id, Number(lessonId));
  const { openModal, closeModal } = useModal();

  function openDeleteModal() {
    const handleDelete = () => {
      deleteSection({});
      closeModal();
    };
    openModal({
      id: 'delete-section',
      title: 'Delete Section',
      content: 'Are you sure you want to delete this section?',
      action: (
        <Stack direction='row' spacing={2}>
          <Button variant='outlined' onClick={closeModal}>
            Cancel
          </Button>
          <Button color='error' onClick={handleDelete}>
            Delete
          </Button>
        </Stack>
      ),
    });
  }

  return (
    <Stack gap={4}>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Box maxWidth={600} flexGrow={1}>
          <EditableInputField
            value={section.topic}
            onSave={(topic: string) => updateTopic({ lessonSection: { topic } })}
            label='Section Topic'
            isLoading={isPending}
            error={error}
            onReset={resetTopicMutation}
            isRequired
            textVariant='titleMedium'
          />
        </Box>
        {showDeleteButton ? (
          <IconButton color='default' onClick={openDeleteModal} aria-label='delete item'>
            <Delete />
          </IconButton>
        ) : null}
      </Stack>
    </Stack>
  );
}

export function SectionDetail({
  currentSectionId,
  isFixedPosition = false,
}: {
  currentSectionId?: number;
  isFixedPosition?: boolean; // indicates section cannot be moved or deleted, e.g. "Closing" section
}) {
  const { enable_section_editing: enableSectionEditing = false } = useFeatures(); // TODO(derick): remove feature flag enableSectionEditing once all Create/Re-order/Delete actions are merged
  const { data: currentSection, isFetching, error } = useSectionQuery(currentSectionId);

  const isMultipleChoice =
    currentSection?.question && currentSection?.wait && currentSection?.wait.multiplechoices?.length > 0;

  return (
    <Stack sx={{ width: '100%' }} key={currentSection?.id}>
      <SectionDetailSkeleton isLoading={isFetching} />

      {/* Header */}
      {currentSection && (
        <>
          <Box p={2}>
            <Header section={currentSection} showDeleteButton={enableSectionEditing && !isFixedPosition} />
          </Box>
          <Divider variant='fullWidth' />
        </>
      )}

      {/* Scroll container */}
      <Stack sx={{ flexGrow: 1, overflowY: 'auto', p: 3, gap: 1 }}>
        {/* Error Display */}
        {error ? <Alert severity='error'>{error.message}</Alert> : null}

        {/* Note: as of 1/2024, a "Section" looks like this:
          { id, topic, explanation, question, wait } where explanation, question, and wait are all "segments" 
          In the near future, the segments in a section will likely be iterable, something more like:
          { id, topic, interactions: [segment1, segment2, ...]} to support adding/removing groups of segments to a section
          This Scroll Container would then iterate through the list step and step/wait segments for a section.
        */}
        {currentSection?.explanation && (
          <Card sx={{ bgcolor: 'surfaceContainer.main' }}>
            <CardContent>
              <Typography variant='titleMedium' gutterBottom>
                Lecture Step
              </Typography>
              <EditSegmentField
                title='Text'
                info='This content explains the topic being taught in this section.'
                segment={currentSection.explanation}
              />
            </CardContent>
          </Card>
        )}
        {currentSection?.question &&
          currentSection.wait &&
          (() => (
            <Card sx={{ bgcolor: 'surfaceContainer.main' }}>
              <CardContent>
                <Stack gap={2}>
                  <Typography variant='titleMedium' gutterBottom>
                    Knowledge Check
                  </Typography>
                  <EditSegmentField
                    title={isMultipleChoice ? 'Multiple Choice Question' : 'Question'}
                    info={
                      isMultipleChoice
                        ? 'After explaining this topic, ask the learner a question to assess their understanding.'
                        : 'Enter the question you want the student to answer.'
                    }
                    segment={currentSection.question}
                    waitSegmentProps={{
                      segment: currentSection.wait,
                      title: isMultipleChoice ? 'Multiple Choice Options' : 'Correct Response(s)',
                      info: isMultipleChoice
                        ? ''
                        : 'Provide an example of a correct response to the question to help our A.I. guide the student during the lesson.',
                    }}
                  />
                </Stack>
              </CardContent>
            </Card>
          ))()}
      </Stack>
    </Stack>
  );
}
